import axios from "../../API/axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShimmerDiv } from "@skeleton";
import SuccessToast from "../SuccessToast";
import WarningToast from "../WarningToast";
import ErrorToast from "../ErrorToast";
import useNewsFeedStore from "Views/NewsFeed/useNewsFeedStore";

const NOTIFICATION_CHANNELS = [
  { columnKey: "web", apiKey: "pushNotification" },
  { columnKey: "app", apiKey: "inApp" },
  { columnKey: "email", apiKey: "email" },
  // { columnKey: "sms", apiKey: "sms" },
];

const PageNotifications = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const language = localStorage.getItem("Lang");
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const [warningToastMessage, setWarningToastMessage] = useState("");
  const [showWarningToast, setShowWarningToast] = useState(false);

  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);

        // -------------------------------------------------------------------------
        const { setIsNewsPageLoaded } = useNewsFeedStore();

        useEffect(() => {
          setIsNewsPageLoaded(true); // enable right panel when entering NewsFeed
      
          return () => setIsNewsPageLoaded(false); // optional cleanup when leaving
        }, [setIsNewsPageLoaded]);
      
        // -------------------------------------------------------------------------

        
  useEffect(() => {
    const pageNotification = async () => {
      const sessionId = localStorage.getItem("sessionId");
      const token = localStorage.getItem("token");

      if (!sessionId || !token) {
        setErrorToastMessage(t("Notifications.Authentication error"));
        setShowErrorToast(true);
        setLoading(false);
        return;
      }
      setLoading(true);

      const data = {
        deviceType: "Web",
        sessionId: sessionId,
        langKey: language === "en" ? "" : language,
      };

      try {
        const response = await axios.post(`/Account/NotificationConfig`, data);
        setNotifications(response.data.responseData || []);
        setToastMessage(
          t("Notifications.Notification settings loaded successfully")
        );
        setShowToast(true);
      } catch (error) {
        if (error.response) {
          if (error.response.status === 404) {
            setWarningToastMessage(
              error.response.data.message ||
                t("Notifications.No notifications found")
            );
            setShowWarningToast(true);
          } else {
            setErrorToastMessage(
              error.response.data.message ||
                t("Notifications.Something went wrong")
            );
            setShowErrorToast(true);
          }
        } else {
          setErrorToastMessage(t("Notifications.Network error"));
          setShowErrorToast(true);
        }
      } finally {
        setLoading(false);
      }
    };

    pageNotification();
  }, [language, t]);

  const updateNotificationStatus = async (notif, key, updatedValue) => {
    const sessionId = localStorage.getItem("sessionId");
    const token = localStorage.getItem("token");

    if (!sessionId || !token) {
      setErrorToastMessage(t("Notifications.Authentication error"));
      setShowErrorToast(true);
      return;
    }

    const updateData = {
      deviceType: "Web",
      sessionId,
      ncid: notif.ncid,
      langKey: language === "en" ? "" : language,
      [key]: updatedValue,
    };

    try {
      const response = await axios.post(
        `/Account/UpdateNotificationConfig?ConfigType=${key}`,
        updateData
      );

      setNotifications((prev) =>
        prev.map((item) =>
          item.ncid === notif.ncid ? { ...item, [key]: updatedValue } : item
        )
      );

      setToastMessage(
        response.data.message ||
          t("Notifications.Notification updated successfully")
      );
      setShowToast(true);
    } catch (error) {
      setErrorToastMessage(t("Notifications.Failed to update notification"));
      setShowErrorToast(true);
    }
  };

  const handleToggle = (notif, apiKey) => {
    if (notif[apiKey] == null) return;
    updateNotificationStatus(notif, apiKey, !notif[apiKey]);
  };

  const renderToggle = (notif, apiKey) => {
    if (notif[apiKey] == null) return null;

    return (
      <div className="form-check form-switch mb-0 p-0 d-flex justify-content-center">
        <input
          className="form-check-input custom-toggle"
          type="checkbox"
          role="switch"
          checked={Boolean(notif[apiKey])}
          disabled={loading}
          onChange={() => handleToggle(notif, apiKey)}
          aria-label={`${notif.title} ${apiKey}`}
        />
      </div>
    );
  };

  const renderShimmerRows = () =>
    Array.from({ length: 4 }, (_, index) => (
      <tr key={`notification-shimmer-${index}`}>
        <td className="notification-col-label">
          <ShimmerDiv height={16} width="55%" rounded={4} className="mb-2" />
          <ShimmerDiv height={12} width="80%" rounded={4} />
        </td>
        <td className="notification-col-channel">
          <div className="d-flex justify-content-center">
            <ShimmerDiv height={22} width={40} rounded={20} />
          </div>
        </td>
        <td className="notification-col-channel">
          <div className="d-flex justify-content-center">
            <ShimmerDiv height={22} width={40} rounded={20} />
          </div>
        </td>
        <td className="notification-col-channel">
          <div className="d-flex justify-content-center">
            <ShimmerDiv height={22} width={40} rounded={20} />
          </div>
        </td>
      </tr>
    ));

  return (
    <>
      <div className="setting-header">
        <h4>{t("Notifications.Notification Setting")}</h4>
      </div>
      <div className="p-3">
        <div className="notificationTable p-0">
          <table width="100%">
            <thead>
              <tr>
                <th className="notification-col-label">
                  {t("Notifications.Notify me when")}
                </th>
                <th className="notification-col-channel">
                  {t("Notifications.Web")}
                </th>
                <th className="notification-col-channel">
                  {t("Notifications.App")}
                </th>
                <th className="notification-col-channel">
                  {t("Notifications.Email")}
                </th>
                {/* SMS column hidden for now
                <th className="notification-col-channel">{t("Notifications.SMS")}</th>
                */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                renderShimmerRows()
              ) : notifications.length > 0 ? (
                notifications.map((notif) => (
                  <tr key={notif.ncid}>
                    <td className="notification-col-label">
                      <div className="notification-title">{notif.title}</div>
                      {notif.description && (
                        <div className="notification-description">
                          {notif.description}
                        </div>
                      )}
                    </td>
                    {NOTIFICATION_CHANNELS.map(({ columnKey, apiKey }) => (
                      <td
                        key={columnKey}
                        className="notification-col-channel"
                      >
                        {renderToggle(notif, apiKey)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-muted">
                    {t("Notifications.No notifications found")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showToast && (
        <SuccessToast
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
      {showWarningToast && (
        <WarningToast
          message={warningToastMessage}
          onClose={() => setShowWarningToast(false)}
        />
      )}
      {showErrorToast && (
        <ErrorToast
          message={errorToastMessage}
          onClose={() => setShowErrorToast(false)}
        />
      )}
    </>
  );
};

export default PageNotifications;
